import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertOctagon,
  AlertTriangle,
  Bell,
  Check,
  Clock,
  EyeOff,
  Inbox,
  Plug,
  ShieldOff,
  Wifi,
  XCircle,
} from "lucide-react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { useToasts } from "./ui/toast";
import { ApiError } from "@/lib/api";
import { relTime, relTimeLong } from "@/lib/time";
import type { Severity, TriageItem as TItem, TriageKind } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

const SEVERITY_TONE: Record<Severity, "danger" | "warning" | "muted"> = {
  urgent: "danger",
  warn: "warning",
  info: "muted",
};

const KIND_ICON: Record<TriageKind, React.ComponentType<{ className?: string }>> = {
  draft_pending: Inbox,
  detection_silenced: EyeOff,
  bridge_silent: Plug,
  missed_self_report: Clock,
  ambiguous_inbound: AlertTriangle,
  paid_dm_approval: Inbox,
  recruiter_escalation: AlertTriangle,
  cost_ceiling_proximity: AlertOctagon,
  gate_a_blocked: ShieldOff,
  send_forbidden: ShieldOff,
  job_failed: XCircle,
};

/**
 * Generic triage item renderer for non-draft kinds. Drafts get the richer
 * DraftCard renderer. This handles: silenced, bridge_silent, missed_self_report,
 * ambiguous_inbound, recruiter_escalation, cost_ceiling_proximity.
 */
export function TriageItemView({
  item,
  focused,
  onAfterAction,
}: {
  item: TItem;
  focused?: boolean;
  onAfterAction?: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const Icon = KIND_ICON[item.kind] ?? Bell;

  const ack = useMutation({
    mutationFn: async (action: { endpoint: string; method: "POST" | "GET" }) => {
      const r = await fetch(action.endpoint, {
        method: action.method,
        headers: { "content-type": "application/json" },
        credentials: "include",
      });
      if (!r.ok) throw new ApiError(r.status, "ack_failed", `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      push({ tone: "success", title: "Acknowledged" });
      qc.invalidateQueries({ queryKey: ["triage"] });
      onAfterAction?.();
    },
    onError: (e) => {
      push({
        tone: "danger",
        title: "Action failed",
        description: (e as Error).message,
      });
    },
  });

  return (
    <Card
      className={cn(
        "transition-shadow",
        focused && "shadow-[0_0_0_1.5px_hsl(var(--brand)/0.55)]",
      )}
    >
      <div className="flex items-start gap-3 p-3">
        <div
          className={cn(
            "mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md text-sm",
            item.severity === "urgent"
              ? "bg-destructive/15 text-destructive"
              : item.severity === "warn"
                ? "bg-warning/15 text-warning"
                : "bg-muted text-muted-foreground",
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge tone={SEVERITY_TONE[item.severity]} className="capitalize">
              {item.severity}
            </Badge>
            <Badge tone="muted" className="font-mono">
              {item.kind}
            </Badge>
            <span className="ml-auto text-2xs text-muted-foreground">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{relTime(item.occurred_at)}</span>
                </TooltipTrigger>
                <TooltipContent>{relTimeLong(item.occurred_at)}</TooltipContent>
              </Tooltip>
            </span>
          </div>
          <div className="mt-1.5 text-sm font-medium text-foreground">{item.title}</div>
          <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {item.subtitle}
          </div>
          {item.creator_id ? (
            <Link
              to={`/creators/${item.creator_id}`}
              className="mt-1 inline-flex items-center gap-1 text-2xs text-brand hover:underline"
            >
              @{item.creator_handle ?? item.creator_id}
            </Link>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-1.5 border-t border-border/60 px-3 py-2">
        {item.actions.length === 0 ? (
          <span className="text-2xs text-muted-foreground">view-only</span>
        ) : (
          item.actions.map((a) => (
            <Button
              key={a.verb + a.endpoint}
              size="xs"
              variant={a.verb.toLowerCase() === "approve" ? "default" : "outline"}
              disabled={ack.isPending}
              onClick={() => ack.mutate(a)}
            >
              {a.verb.toLowerCase() === "approve" ? (
                <Check className="mr-1 h-3 w-3" />
              ) : a.verb.toLowerCase() === "acknowledge" ? (
                <Wifi className="mr-1 h-3 w-3" />
              ) : null}
              {a.verb}
            </Button>
          ))
        )}
      </div>
    </Card>
  );
}
